/**
 * Human-friendly cohort labels — DISPLAY LAYER ONLY.
 *
 * Resolves a plain-language label from a cohort's technical identity (code +
 * name) for display to non-technical users. This NEVER changes cohort
 * identifiers, references, eligibility resolution, or any pipeline — callers
 * keep passing the technical code/name around exactly as before; this only
 * decides what TEXT to show.
 *
 * Resolution order (see Round 0 proposal):
 *   1. Curated OVERRIDES (by exact code or name) — special / hand-named cohorts.
 *   2. Already-friendly names (have spaces/capitals, no ":") → used as-is.
 *   3. Structured machine names "type:slug[:entity-id]" → decoded.
 *   4. Plain hyphenated tokens → Title Cased.
 *   5. Fallback to the technical code (never blank).
 *
 * Because the live cohort set is dynamic/DB-resident (adventure-derived,
 * per-tenant), the decoder — not a hand-enumerated map — covers the bulk; the
 * OVERRIDES map handles the handful of curated exceptions.
 */

export interface CohortIdentity {
  /** cohort_code — e.g. "coh-591e…", "coh-everyone", "cohort-001". Opaque ID. */
  code?: string | null;
  /** cohort_name — e.g. "adventure:exercise:adv-34" or "Diabetes Management". */
  name?: string | null;
}

/** Curated overrides keyed by lowercased code OR name. Extend as cohorts need it. */
const OVERRIDES: Record<string, string> = {
  everyone: 'Everyone',
  'coh-everyone': 'Everyone',
  // The Delta 4 universal sentinel. It is resolved to the environment's real
  // everyone cohort at publish, so it never reaches the platform — but it does
  // reach the screen, where "cht-all" means nothing to anybody.
  'cht-all': 'Everyone',
  'all-members': 'All members',
};

/** Leading "type" segment → natural-language qualifier shown in parentheses. */
const TYPE_LABELS: Record<string, string> = {
  adventure: 'Adventure',
  plan: 'Plan',
  segment: 'Segment',
  filter: 'Filter',
};

const titleCase = (s: string): string =>
  s
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

/**
 * A name already meant for humans: has a space or capital, and carries neither
 * of the two separators that mark a machine slug.
 *
 * The "_" test matters as much as the ":" one. Without it a single capital
 * anywhere was enough to wave a slug straight through untouched, so
 * "language_es" was title-cased to "Language Es" while "language_en-US",
 * "Survey_Test" and "Watco_chain_task_BOK_App" rendered raw — the same kind of
 * name formatted two different ways in the same dropdown. An underscore is not
 * something a human types into a display name.
 */
const looksFriendly = (name: string): boolean =>
  !name.includes(':') && !name.includes('_') && /[A-Z ]/.test(name);

/**
 * Resolve the friendly display label for a cohort. Pure; safe to call in render.
 */
export function cohortLabel(input: CohortIdentity | null | undefined): string {
  const name = (input?.name ?? '').trim();
  const code = (input?.code ?? '').trim();

  // 1. overrides
  if (code && OVERRIDES[code.toLowerCase()]) return OVERRIDES[code.toLowerCase()];
  if (name && OVERRIDES[name.toLowerCase()]) return OVERRIDES[name.toLowerCase()];

  // 2. already-friendly name
  if (name && looksFriendly(name)) return name;

  // 3. structured "type:slug[:id]" — ID segments are truncated to first 5 hex chars
  //    so users can distinguish cohorts while keeping labels short.
  if (name && name.includes(':')) {
    const parts = name.split(':').map((p) => p.trim()).filter(Boolean);
    const type = (parts[0] ?? '').toLowerCase();
    const slug = parts.slice(1).map((seg) => {
      // "adv-f9d6d562…" or "adv-f9d6d-3a8d-…" → keep prefix + first 5 hex chars
      const prefixed = seg.match(/^([a-z]{2,5}-)([0-9a-f-]{5,})/i);
      if (prefixed) return prefixed[1] + prefixed[2].replace(/-/g, '').slice(0, 5);
      // bare long hex with no prefix
      if (/^[0-9a-f]{6,}$/i.test(seg)) return seg.slice(0, 5);
      return seg;
    }).join(' ');
    const typeLabel = TYPE_LABELS[type];
    if (slug) {
      const friendly = titleCase(slug);
      return typeLabel ? `${friendly} (${typeLabel})` : friendly;
    }
    return typeLabel ?? titleCase(name.replace(/:/g, ' '));
  }

  // 4. plain hyphenated token
  if (name) return titleCase(name);

  // 5. fallback — never blank
  return code || '(unnamed cohort)';
}

/** The raw technical identity for tooltip / detail view (never shown at rest). */
export function cohortTechnicalIdentity(input: CohortIdentity | null | undefined): string {
  const parts: string[] = [];
  if (input?.name) parts.push(input.name);
  if (input?.code) parts.push(input.code);
  return parts.join('  ·  ') || '(no technical identifier)';
}

/** True when this cohort fell through to the fallback (no friendly label resolved). */
export function isUnmappedCohort(input: CohortIdentity | null | undefined): boolean {
  const name = (input?.name ?? '').trim();
  const code = (input?.code ?? '').trim();
  const label = cohortLabel(input);
  return label === code || label === '(unnamed cohort)' || (!name && !!code);
}

/**
 * Search match: power users may paste a coh- id, so match the friendly label AND
 * the raw technical name AND the code (case-insensitive).
 */
export function cohortSearchMatches(input: CohortIdentity | null | undefined, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [cohortLabel(input), input?.name ?? '', input?.code ?? ''].join(' ').toLowerCase();
  return hay.includes(q);
}
